// react
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// types
import { Account, Entity, Loan, Option } from '../../../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../../../hooks/useFetch';

// context
import { ContextProject, ContextUserProjectPermissions } from '../../Project.context';

// mui
import { Box, Container, Grid, Typography } from '@mui/material';

// icons
import { SwapHoriz as IconFundFlow } from '@mui/icons-material';

// pages
import { Entities } from './Entities/Entities';
import { Sankey } from './Sankey/Sankey';
import { Sources } from './Sources/Sources';
import { Summary } from './Summary/Summary';
import { Transactions } from './Transactions/Transactions';

// tables
import { ChartSankeyProps } from '../../../../charts/ChartSankey/ChartSankey';

// ui
import { FilterChangeEvent, FilterProps } from '../../../../ui/Filters/Filters';

// v2 listing header
import { HeaderListingPage } from '../../../../ui/HeaderListingPage/HeaderListingPage';

// utils
import { formatDate } from '../../../../../utils/dates';
import { translateType } from '../../../../../utils/translations';

export interface FilterValuesProps {
	entities: Option[];
}

export interface FiltersValuesDefaults {
	accounts?: string[];
	currency?: string;
	dateAfter?: string;
	dateBefore: string;
	entities?: Option[];
	loans?: string[];
	type?: string[];
}

export const FundFlow = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const { t } = useTranslation();

	// params
	const [searchParams, setSearchParams] = useSearchParams();
	const paramAccounts = searchParams.get('accounts');
	const paramDateAfter = searchParams.get('dateAfter');
	const paramDateBefore = searchParams.get('dateBefore');
	const paramCurrency = searchParams.get('currency');
	const paramEntities = searchParams.get('entities');
	const paramLoans = searchParams.get('loans');
	const paramType = searchParams.get('type');

	// defaults
	const defaultFiltersValues: FiltersValuesDefaults = useMemo(() => {
		return {
			accounts: [],
			currency: '',
			dateAfter: '',
			dateBefore: '',
			entities: [],
			loans: [],
			type: [],
		};
	}, []);

	// state
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [dataSankey, setDataSankey] = useState<ChartSankeyProps | null>(null);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [filtersValues, setFiltersValues] = useState(defaultFiltersValues);

	// fetch sankey
	let fetchSankeyUrl = `${process.env.REACT_APP_API_URL}/charts/sankey?project=${project?.id}`;

	if (paramAccounts) {
		fetchSankeyUrl += `&accounts=${decodeURIComponent(paramAccounts)}`;
	}

	if (paramCurrency) {
		fetchSankeyUrl += `&currency=${decodeURIComponent(paramCurrency)}`;
	}

	if (paramDateAfter) {
		fetchSankeyUrl += `&dateAfter=${paramDateAfter}`;
	}

	if (paramDateBefore) {
		fetchSankeyUrl += `&dateBefore=${paramDateBefore}`;
	}

	if (paramEntities) {
		fetchSankeyUrl += `&entities=${decodeURIComponent(paramEntities)}`;
	}

	if (paramLoans) {
		fetchSankeyUrl += `&loans=${decodeURIComponent(paramLoans)}`;
	}

	if (paramType) {
		fetchSankeyUrl += `&type=${decodeURIComponent(paramType)}`;
	}

	// vars
	const idFormFilters = 'form-sankey-filters';

	// fetch
	const { refetch: refetchSankey } = useFetch({
		isEnabled: Boolean(project && paramCurrency),
		url: fetchSankeyUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			let dataSankeyNew = null;
			if (res.data?.links?.length && res.data?.nodes?.length) {
				dataSankeyNew = {
					data: {
						...res.data,
					},
				};
			}

			setDataSankey(dataSankeyNew);
		},
	});

	// fetch loans
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=id,code`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch entities
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,type`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch accounts
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&fields=id,name,currency`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setAccounts(res.data);
		},
	});

	// options
	const sortByLabel = (a: Option, b: Option) => {
		return String(a.label) > String(b.label) ? 1 : -1;
	};

	const optionsLoans: Option[] = loans
		.map((loan) => {
			return {
				disabled: loan.status === 'DISABLED',
				label: loan.code,
				value: String(loan.id),
			};
		})
		.sort(sortByLabel);

	const optionsEntities: Option[] = entities
		.map((entity) => {
			return {
				label: entity.name,
				secondary: translateType(entity.type, t),
				value: String(entity.id),
			};
		})
		.sort(sortByLabel);

	const optionsAccounts: Option[] = accounts
		.map((account) => {
			return {
				label: account.name,
				value: String(account.id),
			};
		})
		.sort(sortByLabel);

	const currencies = accounts.map((account) => account.currency);
	const optionsCurrency: Option[] = currencies
		.filter((item, index) => currencies.indexOf(item) === index)
		.map((currency) => {
			return {
				label: currency,
				value: currency,
			};
		})
		.sort(sortByLabel);

	const optionsTransactionTypes = useMemo(() => {
		const options: Option[] = [];

		if (userProjectPermissions?.payment?.read) {
			options.push({
				label: t('payment'),
				value: 'PAYMENT',
			});
		}

		if (userProjectPermissions?.cashBenefit?.read) {
			options.push({
				label: t('cashBenefit'),
				value: 'CASH_BENEFIT',
			});
		}

		if (userProjectPermissions?.transfer?.read) {
			options.push({
				label: t('transfer'),
				value: 'TRANSFER',
			});
		}

		return options;
	}, [t, userProjectPermissions]);

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent, name: string, value: unknown) => {
		let param = value as string;

		if (Array.isArray(value)) {
			if (typeof value[0] === 'object') {
				param = value.map((option) => option.value).join(',');
			} else {
				param = value.join(',');
			}
		}

		// set search params and state
		searchParams.set(name, param);
		setSearchParams(searchParams);
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// required for full screen mode
	const MenuProps = {
		MenuProps: {
			disablePortal: true,
		},
	};

	// filters
	const filters: FilterProps[] = [
		{
			hasAllOption: true,
			id: `${idFormFilters}-type`,
			label: t('type'),
			multiple: true,
			name: 'type',
			onChange: (e, value) => handleChangeFilterValue(e, 'type', value),
			options: optionsTransactionTypes,
			type: 'SELECT',
			value: filtersValues.type,
			...MenuProps,
		},
		{
			hasNoneOption: false,
			id: `${idFormFilters}-currency`,
			label: t('currency'),
			name: 'currency',
			onChange: (e, value) => handleChangeFilterValue(e, 'currency', value),
			options: optionsCurrency,
			type: 'SELECT',
			value: filtersValues.currency,
			...MenuProps,
		},
		{
			id: `${idFormFilters}-date-after`,
			inputProps: {
				// date cannot be before a year from current date
				min: formatDate({
					date: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
					format: 'YYYY-MM-DD',
				}),
				// date cannot be after before date or current date if dateBefore is not set
				max: filtersValues.dateBefore || formatDate({ date: new Date(), format: 'YYYY-MM-DD' }),
			},
			label: t('startDate'),
			name: 'dateAfter',
			onChange: (e, value) => handleChangeFilterValue(e, 'dateAfter', value),
			type: 'DATE',
			value: filtersValues.dateAfter,
		},
		{
			id: `${idFormFilters}-date-before`,
			inputProps: {
				// date cannot be before after date
				min: filtersValues.dateAfter,
				// date cannot be after current date
				max: new Date().toISOString().split('T')[0],
			},
			label: t('endDate'),
			name: 'dateBefore',
			onChange: (e, value) => handleChangeFilterValue(e, 'dateBefore', value),
			type: 'DATE',
			value: filtersValues.dateBefore,
		},
		{
			hasAllOption: true,
			id: `${idFormFilters}-loans`,
			label: t('financingSources'),
			multiple: true,
			name: 'loans',
			onChange: (e, value) => handleChangeFilterValue(e, 'loans', value),
			options: optionsLoans,
			type: 'SELECT',
			value: filtersValues.loans,
			...MenuProps,
		},
		{
			disablePortal: true,
			hasAllOption: true,
			id: `${idFormFilters}-entities`,
			label: t('entities'),
			name: 'entities',
			onChange: (e, value) => handleChangeFilterValue(e, 'entities', value),
			options: optionsEntities,
			type: 'AUTOCOMPLETE',
			value: filtersValues.entities,
		},
		{
			hasAllOption: true,
			id: `${idFormFilters}-accounts`,
			label: t('accounts'),
			multiple: true,
			name: 'accounts',
			onChange: (e, value) => handleChangeFilterValue(e, 'accounts', value),
			options: optionsAccounts,
			type: 'SELECT',
			value: filtersValues.accounts,
			...MenuProps,
		},
	];

	const getDefaultFiltersValues = useCallback(() => {
		const valuesNew: FiltersValuesDefaults = {
			...defaultFiltersValues,
			type: optionsTransactionTypes.map((option) => option.value as string),
		};

		if (accounts) {
			let accountsNew = accounts.map((a) => String(a.id));
			if (paramAccounts) {
				accountsNew = paramAccounts.split(',');
			}
			valuesNew.accounts = accountsNew;
		}

		if (entities) {
			let entitiesSelected = entities;

			if (paramEntities) {
				const paramEntitiesArray = paramEntities.split(',');
				entitiesSelected = entities.filter((entity) =>
					paramEntitiesArray.includes(String(entity.id))
				);
			}

			valuesNew.entities = entitiesSelected.map((entity) => {
				return {
					label: entity.name,
					value: String(entity.id),
				};
			});
		}

		if (loans) {
			let loansNew = loans.map((a) => String(a.id));
			if (paramLoans) {
				loansNew = paramLoans.split(',');
			}
			valuesNew.loans = loansNew;
		}

		if (paramCurrency) {
			valuesNew.currency = paramCurrency;
		}

		if (paramType) {
			valuesNew.type = paramType.split(',');
		}

		return valuesNew;
	}, [
		accounts,
		defaultFiltersValues,
		entities,
		loans,
		optionsTransactionTypes,
		paramAccounts,
		paramCurrency,
		paramEntities,
		paramLoans,
		paramType,
	]);

	// refetch sankey data when search params change
	useEffect(() => {
		if (paramCurrency) {
			refetchSankey();
		}
	}, [refetchSankey, paramCurrency, searchParams]);

	// set default search params
	useEffect(() => {
		if (!paramCurrency) {
			if (!paramCurrency && currencies.length) {
				searchParams.set('currency', String(currencies[0]));
			}

			setSearchParams(searchParams);
		}
	}, [currencies, defaultFiltersValues, paramCurrency, searchParams, setSearchParams]);

	// set default filters
	useEffect(() => {
		const filtersValuesNew = getDefaultFiltersValues();

		setFiltersValues((filtersValuesOld) => {
			return {
				...filtersValuesOld,
				...filtersValuesNew,
			};
		});
	}, [
		accounts,
		entities,
		getDefaultFiltersValues,
		loans,
		paramAccounts,
		paramCurrency,
		paramEntities,
		paramLoans,
		paramType,
	]);

	return (
		<>
			{(project?.version === '2') && (
				<HeaderListingPage
					icon={IconFundFlow}
					title={t('fundFlow')}
					subtitle={t('reports.fundFlow.subtitle')}
					variant="payment"
				/>
			)}
			<Box sx={{ pt: (project?.version === '2') ? 3 : 0 }}>
			<Grid container spacing={2}>
				{project?.version !== '2' && (
					<Grid item xs={12}>
						<Container maxWidth={false}>
							<Typography variant="h2">{t('fundFlow')}</Typography>
						</Container>
					</Grid>
				)}
				<Grid item xs={12}>
					<Summary dataSankey={dataSankey} />
				</Grid>
			<Grid item xs={12}>
				<Sankey
					dataSankey={dataSankey}
					FiltersProps={{ filters }}
					onResetFilters={() => {
						const filtersValuesNew = getDefaultFiltersValues();
						setFiltersValues(filtersValuesNew);
					}}
				/>
			</Grid>
			<Grid item xs={12}>
				<Container maxWidth={false}>
					<Grid container spacing={2}>
						<Grid item xs={12} lg={4}>
							<Entities dataSankey={dataSankey} />
						</Grid>
						<Grid item xs={12} lg={4}>
							<Transactions dataSankey={dataSankey} />
						</Grid>
						<Grid item xs={12} lg={4}>
							<Sources dataSankey={dataSankey} />
						</Grid>
					</Grid>
				</Container>
			</Grid>
		</Grid>
		</Box>
		</>
	);
};
